import React from 'react';
import '../../../styles/MeepMeep.css';
import { MeepMeepBadge } from '../common/MeepMeepBadge';

interface ParityMetric {
  id: string;
  label: string;
  value: string | number;
  threshold?: string | number;
  status: 'PASS' | 'FAIL' | 'UNKNOWN';
  category?: 'SIGNAL' | 'EXECUTION' | 'DATA';
}

interface RepresentativeDelta {
  timestamp: string;
  description: string;
  research_value: string | number;
  production_value: string | number;
  severity: 'WARNING' | 'BLOCKING';
  category: 'SIGNAL' | 'TRADE' | 'FILL';
}

interface ParityReportProps {
  status: 'PASS' | 'FAIL' | 'UNKNOWN';
  export_blocked: boolean;
  strategyName: string;
  date: string;
  metrics: ParityMetric[] | Record<string, Omit<ParityMetric, 'id'>>;
  deltas: RepresentativeDelta[];
}

export const ParityReportWidget: React.FC<ParityReportProps> = ({
  status,
  export_blocked,
  strategyName,
  date,
  metrics,
  deltas
}) => {
  // Normalize metrics to array if it comes as a dictionary (new parity_report.ui contract)
  const normalizedMetrics = React.useMemo(() => {
    if (Array.isArray(metrics)) return metrics;
    if (typeof metrics === 'object' && metrics !== null) {
      return Object.entries(metrics).map(([id, m]) => ({
        id,
        ...m as any
      })) as ParityMetric[];
    }
    return [];
  }, [metrics]);

  return (
    <div className="meep-parity-widget animate-fade-in">
      {/* Header Section */}
      <div className="meep-parity-header">
        <div>
          <h1 className="text-2xl font-bold mb-1">{strategyName}</h1>
          <p className="text-muted text-sm">Parity Run: {date}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
            <div className={`meep-status-large ${status === 'PASS' ? 'text-emerald-500' : 'text-amber-500'}`}>
              <MeepMeepBadge type={status === 'PASS' ? 'parity-pass' : 'parity-fail'} className="scale-125" />
              {status === 'PASS' ? 'RECONCILED' : 'DISCREPANCIES FOUND'}
            </div>
            <div className={`text-[10px] font-bold px-2 py-0.5 rounded border ${export_blocked ? 'border-red-500/50 text-red-500 bg-red-500/10' : 'border-emerald-500/50 text-emerald-500 bg-emerald-500/10'}`}>
                {export_blocked ? 'EXPORT BLOCKED' : 'EXPORT ELIGIBLE'}
            </div>
        </div>
      </div>

      {/* Metrics Section */}
      <div className="meep-metrics-grid">
        {normalizedMetrics.map(metric => (
          <div key={metric.id} className="meep-glass-card meep-metric-card">
            <div className="flex justify-between items-start mb-1">
                <span className="meep-metric-label">{metric.label}</span>
                {metric.category && (
                    <span className="text-[9px] opacity-40 uppercase tracking-wider">{metric.category}</span>
                )}
            </div>
            <div className="flex items-center justify-between">
              <span className={`meep-metric-value ${metric.status === 'FAIL' ? 'text-amber-500' : 'text-emerald-500'}`}>
                {metric.value}
              </span>
              {metric.threshold && (
                <span className="text-xs text-muted">target: {metric.threshold}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Deltas / Comparison Section */}
      <div className="meep-glass-card p-6">
        <h3 className="text-lg font-semibold mb-4">Representative Mismatches</h3>
        <div className="meep-comparison-header">
          <span className="w-1/4">Timestamp (UTC)</span>
          <span className="w-1/4">Description</span>
          <span className="w-1/4 text-center">Research</span>
          <span className="w-1/4 text-center">Production</span>
        </div>
        
        <table className="meep-parity-table">
          <tbody>
            {deltas.map((delta, i) => (
              <tr key={i} className="meep-parity-row">
                <td className="p-3 w-1/4 text-xs font-mono">{delta.timestamp}</td>
                <td className="p-3 w-1/4">
                  <div className="flex flex-col">
                    <span className="text-sm">{delta.description}</span>
                    <span className={`text-[10px] uppercase font-bold ${delta.severity === 'BLOCKING' ? 'text-red-500' : 'text-amber-500'}`}>
                      {delta.severity} • {delta.category}
                    </span>
                  </div>
                </td>
                <td className="p-3 w-1/4 text-center meep-val-research bg-cyan-500/5 rounded-l">{delta.research_value}</td>
                <td className="p-3 w-1/4 text-center meep-val-prod bg-white/5 rounded-r">{delta.production_value}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {deltas.length === 0 && (
          <div className="py-8 text-center text-muted italic">
            No significant deltas found in this sample.
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="flex justify-between items-center px-4">
        <div className="text-xs text-muted max-w-md">
          <p>This report is generated by the <code>MeepMeep ParityAnalyzer</code> comparing the 3-Trayport research stack against the MoneyMaker plugin runtime.</p>
        </div>
        <button className="meep-glass-card border px-4 py-2 text-sm hover:border-cyan-500/50">
          Export Full Parity Artifact (.xlsx)
        </button>
      </div>
    </div>
  );
};
